/**
 * native.js — native device capabilities for the Capacitor shell.
 * Every helper no-ops (or falls back to a web equivalent) outside the
 * native app, so the same code runs on web + iOS + Android.
 * Plugins are dynamically imported so the web bundle stays lean.
 */
import { isNativeApp, getPlatform } from "@/lib/nativeBridge";

/** Opens the native camera / photo-library action sheet and returns a
 *  File ready for the existing upload pipeline, or null (cancel/web). */
export async function takeNativePhoto() {
  if (!isNativeApp()) return null;
  try {
    const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
    const photo = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Prompt,
      quality: 85,
      correctOrientation: true,
    });
    if (!photo?.webPath) return null;
    const blob = await (await fetch(photo.webPath)).blob();
    const ext = photo.format || "jpeg";
    return new File([blob], `photo-${Date.now()}.${ext}`, {
      type: blob.type || "image/jpeg",
    });
  } catch {
    return null; // user cancelled or permission denied
  }
}

/** System share sheet (native) with Web Share API fallback.
 *  Returns true when a share UI was presented. */
export async function nativeShare({ title, text, url }) {
  if (isNativeApp()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title, text, url, dialogTitle: title });
      return true;
    } catch {
      return true; // cancelling the sheet throws — still "handled"
    }
  }
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text, url });
    } catch {
      /* user cancelled */
    }
    return true;
  }
  return false;
}

/** Light haptic tap on key interactions. No-op on web. */
export async function haptic(style = "light") {
  if (!isNativeApp()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: map[style] || ImpactStyle.Light });
  } catch {
    /* plugin unavailable */
  }
}

/** Schedules on-device local notifications (heat cycles, whelpings,
 *  events). notifications: [{id:int, title, body, at:Date}].
 *  Returns {ok, count?, reason?}. */
export async function scheduleDeviceReminders(notifications) {
  if (!isNativeApp()) return { ok: false, reason: "web" };
  if (!notifications.length) return { ok: false, reason: "empty" };
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display === "prompt" || perm.display === "prompt-with-rationale") {
      perm = await LocalNotifications.requestPermissions();
    }
    if (perm.display !== "granted") return { ok: false, reason: "denied" };
    // Re-scheduling: clear our previous reminders first (ids are stable).
    const pending = await LocalNotifications.getPending();
    if (pending?.notifications?.length) {
      await LocalNotifications.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) });
    }
    await LocalNotifications.schedule({
      notifications: notifications.map((n) => ({
        id: n.id,
        title: n.title,
        body: n.body,
        schedule: { at: n.at },
      })),
    });
    return { ok: true, count: notifications.length };
  } catch {
    return { ok: false, reason: "error" };
  }
}

/** Registers for APNs/FCM push. Delegates to the single consolidated
 *  path in nativePush.js (correct endpoint, listeners attached once). */
export async function registerPushNotifications(_api, { silent = false } = {}) {
  if (!isNativeApp()) return false;
  const { registerNativePush } = await import("@/lib/nativePush");
  return registerNativePush({ silent });
}
